import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Send, Loader2, Compass, Save, PanelLeftClose, Plus, MessageSquare, LayoutDashboard } from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { ChatMessage, ChatHistoryMessage, ResponseBlock } from './types';
import { MessageBubble } from './blocks';
import { claudeQueryService, ChatRequestDTO } from '../../../../services/claudeQueryService';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { isViewSpec, summarizeView, type DatasetMap, type ViewSpec } from './view/types';
import { ResultPanel } from './workspace/ResultPanel';
import { ExampleList } from './workspace/ResultEmptyState';
import { hasResult, promptFor } from './workspace/resultUtils';

/** Maks rader per datasett i en lagret samtale (øyeblikksbilde — data hentes på nytt med «Oppdater»). */
const SNAPSHOT_MAX_ROWS = 500;

const snapshotDatasets = (datasets: DatasetMap): DatasetMap =>
    Object.fromEntries(Object.entries(datasets).map(([id, d]) => [id, {
        ...d,
        rows: d.rows.slice(0, SNAPSHOT_MAX_ROWS),
        truncated: d.truncated || d.rows.length > SNAPSHOT_MAX_ROWS
    }]));

interface ChatInterfaceProps {
    conversationId?: number | null;
    onConversationSaved?: (id: number) => void;
    onSaveAsReport?: (sql: string, prompt: string, exportFormat?: string, chartConfig?: string) => void;
    /** Start en ny, tom samtale */
    onNewConversation?: () => void;
}

/** Bredden der samtale og resultat står side om side; smalere skjermer får faner. */
const WIDE_QUERY = '(min-width: 1024px)';

function useIsWide(): boolean {
    const [wide, setWide] = useState(() => typeof window === 'undefined' || window.matchMedia(WIDE_QUERY).matches);
    useEffect(() => {
        const mq = window.matchMedia(WIDE_QUERY);
        const onChange = () => setWide(mq.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);
    return wide;
}

/** Skjult samtale huskes per tenant og operatør (GUI-valg, ikke en innstilling). */
function useChatHidden(customerNo?: string, operatorId?: string): [boolean, (hidden: boolean) => void] {
    const key = `compass.chatHidden.${customerNo ?? ''}.${operatorId ?? ''}`;
    const [hidden, setHiddenState] = useState(() => {
        try { return localStorage.getItem(key) === '1'; } catch { return false; }
    });
    const setHidden = useCallback((value: boolean) => {
        setHiddenState(value);
        try { localStorage.setItem(key, value ? '1' : '0'); } catch { /* lagring utilgjengelig — gjelder økten */ }
    }, [key]);
    return [hidden, setHidden];
}

/**
 * Spør Compass som arbeidsflate: samtalen til venstre, resultatet av valgt svar til høyre i full bredde.
 * Samtalen kan skjules. Under 1024 px blir de to delene faner.
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
    conversationId = null,
    onConversationSaved,
    onSaveAsReport,
    onNewConversation,
}) => {
    const { user } = useAuth();
    const { t } = useTranslation();
    const isWide = useIsWide();
    const [chatHidden, setChatHidden] = useChatHidden(user?.customerNo, user?.operatorId);
    const [mobileTab, setMobileTab] = useState<'chat' | 'result'>('chat');
    const [selectedId, setSelectedId] = useState<string | null>(null);

    const [messages, setMessages] = useState<ChatMessage[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [conversationTitle, setConversationTitle] = useState<string | null>(null);
    const [currentConversationId, setCurrentConversationId] = useState<number | null>(conversationId ?? null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [loadingConversation, setLoadingConversation] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Bare ved nye meldinger — ikke når en visning justeres
    useEffect(() => {
        scrollToBottom();
    }, [messages.length]);

    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 128)}px`;
        }
    }, [inputValue]);

    // Last samtalen når en annen samtale velges. Ikke når id-en kommer fra vår egen første lagring —
    // da ble samtalen lastet på nytt fra serveren, radene kuttet til 500 og svaret merket som øyeblikksbilde.
    const savedConversationIdRef = useRef<number | null>(null);
    useEffect(() => {
        if (conversationId && conversationId > 0 && conversationId !== savedConversationIdRef.current) {
            loadConversation(conversationId);
        }
    }, [conversationId]);

    const loadConversation = async (id: number) => {
        try {
            setLoadingConversation(true);
            const detail = await claudeQueryService.getConversation(id);

            // Parse messages JSON
            if (detail.messages) {
                try {
                    const parsed = JSON.parse(detail.messages) as ChatMessage[];
                    // Restore Date objects
                    const restored = parsed.map(m => ({
                        ...m,
                        timestamp: new Date(m.timestamp),
                        // Visninger i lagrede samtaler er øyeblikksbilder (radene kan være kuttet)
                        snapshot: m.view ? true : undefined,
                    }));
                    setMessages(restored);
                    // Vis det siste resultatet i samtalen
                    const lastResult = [...restored].reverse().find(m => hasResult(m));
                    setSelectedId(lastResult?.id ?? null);
                } catch {
                    setMessages([]);
                }
            }

            setConversationTitle(detail.title || null);
            setCurrentConversationId(detail.conversationId);
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error('Failed to load conversation:', err);
        } finally {
            setLoadingConversation(false);
        }
    };

    // Auto-save: debounce after each assistant response. saveConversation leses via ref, ellers ville
    // timeren kalle versjonen fra første render (med tom meldingsliste).
    const saveConversationRef = useRef<(isAutoSave?: boolean) => Promise<void>>(async () => {});
    const scheduleAutoSave = useCallback(() => {
        if (autoSaveTimerRef.current) {
            clearTimeout(autoSaveTimerRef.current);
        }
        autoSaveTimerRef.current = setTimeout(() => {
            saveConversationRef.current(true);
        }, 3000); // Auto-save 3 seconds after last message
    }, []);

    // Cleanup auto-save timer on unmount
    useEffect(() => {
        return () => {
            if (autoSaveTimerRef.current) {
                clearTimeout(autoSaveTimerRef.current);
            }
        };
    }, []);

    const saveConversation = async (isAutoSave = false) => {
        // Don't save empty conversations or during loading
        const realMessages = messages.filter(m => !m.isLoading);
        if (realMessages.length === 0) return;

        try {
            if (!isAutoSave) setIsSaving(true);

            // Serialize messages to JSON (strip non-serializable data for storage)
            const serializableMessages = realMessages.map(m => ({
                id: m.id,
                role: m.role,
                content: m.content,
                timestamp: m.timestamp,
                blocks: m.blocks,
                generatedSQL: m.generatedSQL,
                executionTimeMs: m.executionTimeMs,
                tokensUsed: m.tokensUsed,
                cacheHit: m.cacheHit,
                suggestedFollowups: m.suggestedFollowups,
                isError: m.isError,
                view: m.view,
                originalView: m.originalView,
                datasets: m.datasets ? snapshotDatasets(m.datasets) : undefined,
            }));

            // Determine title: use first user message or conversationTitle from API
            const title = conversationTitle
                || realMessages.find(m => m.role === 'user')?.content.substring(0, 100)
                || t('manager.modules.claudeQuery.chat.newConversation', {}, 'Ny samtale');

            const result = await claudeQueryService.saveConversation({
                conversationId: currentConversationId && currentConversationId > 0
                    ? currentConversationId
                    : undefined,
                title,
                messages: JSON.stringify(serializableMessages),
                messageCount: realMessages.length,
            });

            if (result.conversationId && result.conversationId !== currentConversationId) {
                setCurrentConversationId(result.conversationId);
                savedConversationIdRef.current = result.conversationId;
                onConversationSaved?.(result.conversationId);
            }

            setConversationTitle(title);
            setHasUnsavedChanges(false);
        } catch (err) {
            console.error('Failed to save conversation:', err);
        } finally {
            if (!isAutoSave) setIsSaving(false);
        }
    };
    saveConversationRef.current = saveConversation;

    const buildConversationHistory = (): ChatHistoryMessage[] => {
        return messages
            .filter(m => !m.isLoading)
            .slice(-10)
            .map(m => ({
                role: m.role as 'user' | 'assistant',
                content: m.content,
                hasData: !!(m.blocks && m.blocks.some(b => b.type === 'table')),
                dataSummary: m.blocks?.find(b => b.type === 'table')?.tableData
                    ? `${m.blocks.find(b => b.type === 'table')!.tableData!.rows.length} rader`
                    : undefined,
                generatedSQL: m.generatedSQL,
                viewSummary: m.view ? summarizeView(m.view, m.datasets) : undefined,
            }));
    };

    const handleSubmit = async (messageText?: string) => {
        const text = messageText || inputValue.trim();
        if (!text || isLoading || !user?.companyNo) return;

        const userMessage: ChatMessage = {
            id: Date.now().toString(),
            role: 'user',
            content: text,
            timestamp: new Date(),
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setIsLoading(true);
        setHasUnsavedChanges(true);

        // Auto-derive title from first user message
        if (!conversationTitle && messages.length === 0) {
            setConversationTitle(text.substring(0, 100));
        }

        const loadingId = (Date.now() + 1).toString();
        setMessages(prev => [...prev, {
            id: loadingId,
            role: 'assistant',
            content: '',
            timestamp: new Date(),
            isLoading: true,
        }]);

        try {
            const request: ChatRequestDTO = {
                message: text,
                conversationHistory: buildConversationHistory(),
                maxRows: 100,
            };

            const response = await claudeQueryService.sendMessage(request);

            const assistantMessage: ChatMessage = {
                id: loadingId,
                role: 'assistant',
                content: response.message,
                timestamp: new Date(),
                blocks: response.blocks as ResponseBlock[],
                generatedSQL: response.generatedSQL,
                executionTimeMs: response.executionTimeMs,
                tokensUsed: response.tokensUsed,
                cacheHit: response.cacheHit,
                suggestedFollowups: response.suggestedFollowups,
                isError: !response.success,
                view: isViewSpec(response.view) ? response.view : undefined,
                datasets: response.datasets as DatasetMap | undefined,
            };

            setMessages(prev => prev.map(m => m.id === loadingId ? assistantMessage : m));
            // Nytt resultat vises med en gang (på smal skjerm: bytt til resultatfanen)
            if (hasResult(assistantMessage)) {
                setSelectedId(loadingId);
                if (!isWide) setMobileTab('result');
            }

            // Update title from API response if provided
            if (response.conversationTitle) {
                setConversationTitle(response.conversationTitle);
            }

            // Schedule auto-save after successful response
            scheduleAutoSave();
        } catch (error) {
            const errorMessage: ChatMessage = {
                id: loadingId,
                role: 'assistant',
                content: error instanceof Error ? error.message : t('manager.modules.claudeQuery.chat.communicationError', {}, 'Det oppstod en feil ved kommunikasjon med serveren.'),
                timestamp: new Date(),
                isError: true,
            };

            setMessages(prev => prev.map(m => m.id === loadingId ? errorMessage : m));
        } finally {
            setIsLoading(false);
        }
    };

    const handleFormSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        handleSubmit();
    };

    const handleViewChange = useCallback((messageId: string, view: ViewSpec, datasets: DatasetMap, originalView: ViewSpec) => {
        setMessages(prev => prev.map(m => m.id === messageId ? { ...m, view, datasets, originalView } : m));
        setHasUnsavedChanges(true);
        scheduleAutoSave();
    }, [scheduleAutoSave]);

    const resultMessages = useMemo(() => messages.filter(m => hasResult(m)), [messages]);
    const selected = resultMessages.find(m => m.id === selectedId) ?? null;
    const position = selected ? resultMessages.indexOf(selected) + 1 : 0;
    const selectResult = (id: string) => {
        setSelectedId(id);
        if (!isWide) setMobileTab('result');
    };

    const lastAssistantMessage = [...messages].reverse().find(m => m.role === 'assistant' && !m.isLoading);
    const suggestedFollowups = lastAssistantMessage?.suggestedFollowups?.filter(Boolean) || [];
    const showChat = isWide ? !chatHidden : mobileTab === 'chat';
    const showResult = isWide || mobileTab === 'result';

    const chatColumn = (
        <section className={`${isWide ? 'w-[380px] xl:w-[420px] flex-shrink-0' : 'flex-1'} min-w-0 flex flex-col bg-white rounded-lg shadow`}>
            {/* Samtalehode: tittel, lagring, ny samtale og skjul */}
            <div className="flex items-center gap-1 px-3 py-1 border-b bg-gray-50 rounded-t-lg">
                <span className="flex-1 min-w-0 text-xs text-gray-500 truncate">
                    {conversationTitle || t('manager.modules.claudeQuery.chat.newConversation', {}, 'Ny samtale')}
                </span>
                {messages.length > 0 && (
                    <button
                        onClick={() => saveConversation(false)}
                        disabled={isSaving || !hasUnsavedChanges}
                        className={`flex items-center gap-1 min-h-[44px] px-2 rounded text-xs transition-colors ${hasUnsavedChanges
                            ? 'text-blue-600 hover:bg-blue-50'
                            : 'text-gray-400'}`}
                    >
                        {isSaving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                        {hasUnsavedChanges ? t('common.buttons.save', {}, 'Lagre') : t('manager.modules.claudeQuery.chat.saved', {}, 'Lagret')}
                    </button>
                )}
                {onNewConversation && messages.length > 0 && (
                    <button
                        onClick={onNewConversation}
                        className="flex items-center gap-1 min-h-[44px] px-2 rounded text-xs text-gray-600 hover:bg-gray-100"
                    >
                        <Plus size={14} />
                        {t('manager.modules.claudeQuery.workspace.newConversation', {}, 'Ny')}
                    </button>
                )}
                {isWide && (
                    <button
                        onClick={() => setChatHidden(true)}
                        className="flex items-center justify-center min-w-[44px] min-h-[44px] rounded text-gray-500 hover:bg-gray-100"
                        aria-label={t('manager.modules.claudeQuery.workspace.hideChat', {}, 'Skjul samtalen')}
                    >
                        <PanelLeftClose size={16} />
                    </button>
                )}
            </div>

            {/* Meldinger */}
            <div className="flex-1 overflow-y-auto p-3 space-y-3">
                {loadingConversation ? (
                    <div className="flex flex-col items-center justify-center h-full text-gray-400">
                        <Loader2 size={24} className="animate-spin text-blue-500 mb-3" />
                        <p className="text-sm">{t('manager.modules.claudeQuery.chat.loadingConversation', {}, 'Laster samtale...')}</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="flex flex-col items-center text-center pt-6">
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center mb-3">
                            <Compass size={24} className="text-white" />
                        </div>
                        <p className="text-base font-medium mb-1 text-gray-700">{t('manager.modules.claudeQuery.chat.greeting', {}, 'Hei! Jeg er Compass')}</p>
                        <p className="text-sm text-gray-500 mb-4 px-4">
                            {isWide
                                ? t('manager.modules.claudeQuery.workspace.greetingWide', {}, 'Skriv et spørsmål nedenfor, eller velg et eksempel til høyre.')
                                : t('manager.modules.claudeQuery.workspace.greetingNarrow', {}, 'Skriv et spørsmål nedenfor, eller velg et eksempel.')}
                        </p>
                        {!isWide && <div className="w-full text-left"><ExampleList onAsk={(q) => handleSubmit(q)} compact /></div>}
                    </div>
                ) : null}

                {messages.map((message) => {
                    if (message.isLoading) {
                        return (
                            <div key={message.id} className="flex justify-start">
                                <div className="mr-2 mt-1 flex-shrink-0">
                                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                                        <Compass size={14} className="text-white" />
                                    </div>
                                </div>
                                <div className="bg-white border border-gray-200 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm">
                                    <div className="flex items-center gap-2">
                                        <Loader2 size={16} className="animate-spin text-blue-600" />
                                        <span className="text-sm text-gray-500">{t('manager.modules.claudeQuery.chat.thinking', {}, 'Tenker...')}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    }
                    return (
                        <MessageBubble
                            key={message.id}
                            message={message}
                            isSelected={message.id === selected?.id}
                            onSelectResult={selectResult}
                        />
                    );
                })}

                <div ref={messagesEndRef} />
            </div>

            {/* Forslag til neste spørsmål */}
            {suggestedFollowups.length > 0 && !isLoading && (
                <div className="px-3 pb-2 flex gap-2 flex-wrap">
                    {suggestedFollowups.map((followup, idx) => (
                        <button
                            key={idx}
                            onClick={() => handleSubmit(followup)}
                            className="min-h-[44px] px-3 py-1.5 text-xs text-left bg-gray-100 text-gray-700 rounded-2xl hover:bg-gray-200 transition-colors border border-gray-200"
                        >
                            {followup}
                        </button>
                    ))}
                </div>
            )}

            {/* Spørsmålsfelt */}
            <div className="border-t p-3 bg-gray-50 rounded-b-lg">
                <form onSubmit={handleFormSubmit} className="flex gap-2">
                    <textarea
                        ref={textareaRef}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                handleSubmit();
                            }
                        }}
                        enterKeyHint="send"
                        placeholder={t('manager.modules.claudeQuery.chat.inputPlaceholder', {}, 'Still et spørsmål om lagerdataene...')}
                        className="flex-1 px-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none min-h-[48px] max-h-32 text-sm"
                        rows={1}
                        disabled={isLoading || loadingConversation}
                    />
                    <button
                        type="submit"
                        disabled={!inputValue.trim() || isLoading || loadingConversation}
                        className="min-w-[48px] px-3 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
                        aria-label={t('manager.modules.claudeQuery.workspace.send', {}, 'Send')}
                    >
                        {isLoading ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
                    </button>
                </form>
                {isWide && (
                    <p className="text-xs text-gray-400 mt-1.5 text-center">
                        {t('manager.modules.claudeQuery.chat.inputHint', {}, 'Enter for å sende, Shift+Enter for ny linje')}
                    </p>
                )}
            </div>
        </section>
    );

    return (
        <div className="h-full flex flex-col gap-3">
            {/* Faner på smal skjerm (nettbrett i stående format) */}
            {!isWide && (
                <div className="flex bg-gray-100 rounded-lg p-1 flex-shrink-0" role="tablist">
                    {([
                        ['chat', MessageSquare, t('manager.modules.claudeQuery.workspace.tabChat', {}, 'Samtale')],
                        ['result', LayoutDashboard, t('manager.modules.claudeQuery.workspace.tabResult', {}, 'Resultat')],
                    ] as const).map(([tab, Icon, label]) => (
                        <button
                            key={tab}
                            role="tab"
                            aria-selected={mobileTab === tab}
                            onClick={() => setMobileTab(tab)}
                            className={`flex-1 flex items-center justify-center gap-1.5 min-h-[44px] rounded-md text-sm font-medium transition-colors ${mobileTab === tab
                                ? 'bg-white text-blue-600 shadow-sm'
                                : 'text-gray-500'}`}
                        >
                            <Icon size={16} />
                            {label}
                            {tab === 'result' && resultMessages.length > 0 && (
                                <span className="text-xs text-gray-400">({resultMessages.length})</span>
                            )}
                        </button>
                    ))}
                </div>
            )}
            <div className="flex-1 min-h-0 flex gap-3">
                {showChat && chatColumn}
                {showResult && (
                    <section className="flex-1 min-w-0 bg-white rounded-lg shadow overflow-hidden">
                        <ResultPanel
                            message={selected}
                            userPrompt={selected ? promptFor(messages, selected.id) : ''}
                            loading={isLoading}
                            position={position}
                            count={resultMessages.length}
                            onPrevious={() => position > 1 && setSelectedId(resultMessages[position - 2].id)}
                            onNext={() => position < resultMessages.length && setSelectedId(resultMessages[position].id)}
                            onSaveAsReport={onSaveAsReport}
                            onAsk={(q) => handleSubmit(q)}
                            onViewChange={handleViewChange}
                            chatHidden={isWide && chatHidden}
                            onShowChat={() => setChatHidden(false)}
                            showTechnical={!!user?.isDeveloper}
                            singleScroll={!isWide}
                        />
                    </section>
                )}
            </div>
        </div>
    );
};
